import React, { useCallback, useEffect, useState } from 'react'
import {
  PlaidLinkOnEvent,
  PlaidLinkOnEventMetadata,
  PlaidLinkOptions,
  PlaidLinkStableEvent,
  usePlaidLink,
} from 'react-plaid-link'
import { useNavigate } from 'react-router-dom'
import { Analytics } from '@genoa/analytics'
import { VerificationType } from '@genoa/domain'
import { GENERIC_ERROR_MODAL_CONTENT as errorContent } from '@genoa/screen-content'
import { useQueryError } from '@genoa/state-management'
import { getDateTime } from '@genoa/utils'

import { useAuthState } from '../../../../../contexts'
import { useShowErrorMessageModal } from '../../../../../hooks'
import { useOfferState } from '../../../../../modules/flex2/use-state'
import { useStartVerificationMutation } from '../../../../../modules/flexApi'
import { loggerV2, useAnalytics } from '../../../../../providers'
import * as Routes from '../../../../../routing/constants'
import { UploadPaystubs } from './UploadPaystubs'

export const UploadPaystubsContainer = () => {
  const analytics = useAnalytics()
  const { uid } = useAuthState()
  const navigate = useNavigate()
  const offerState = useOfferState()
  const today = getDateTime()
  const { showErrorMessage } = useShowErrorMessageModal()

  const [linkToken, setLinkToken] = useState('')
  const [plaidIsOpen, setPlaidIsOpen] = useState(false)

  const [startVerification, { isLoading, error: startVerificationError }] = useStartVerificationMutation()

  const pastThirtyDaysDate = today.minus({ days: 30 }).toLocaleString()
  const getNextBpMonth = () => {
    const tMinus2 = today.set({ day: today.daysInMonth - 2 })
    const tPlus3 = today.plus({ month: 1 }).set({ day: 3 })
    if (tMinus2.toMillis() <= today.toMillis() && today.toMillis() < tPlus3.toMillis()) {
      return tPlus3.monthLong
    }
    return undefined
  }

  const onClickContinue = () => {
    analytics.logEvent(Analytics.Events.INCOME_VERIFICATION_UPLOAD_PAYSTUBS_CTA_CLICKED)

    if (!plaidIsOpen) {
      setPlaidIsOpen(true)
      open()
    }
  }

  const startVerificationForUploadPaystubs = async () => {
    const response = await startVerification({
      customerId: uid!,
      verificationId: offerState.offer.onboarding_income_verification_id!,
      verification_type: VerificationType.DOCUMENT,
    }).unwrap()

    if (response.data?.link_token) {
      analytics.logEvent(Analytics.Events.IV_START_VERIFICATION_SUCCESS)

      setLinkToken(response.data.link_token)
    }
  }

  useQueryError(startVerificationError, {
    onAllErrors() {
      analytics.logEvent(Analytics.Events.IV_START_VERIFICATION_FAILURE)

      showErrorMessage(errorContent.TITLE, errorContent.SUBTITLE)
    },
  })
  useEffect(() => {
    if (linkToken === '') {
      startVerificationForUploadPaystubs()
    }
  }, [linkToken])

  const onUploadPaystubsEvent = useCallback<PlaidLinkOnEvent>(
    (eventName: PlaidLinkStableEvent | string, metadata: PlaidLinkOnEventMetadata) => {
      if (eventName === 'ERROR' || eventName === 'EXIT') {
        loggerV2.error(
          `Plaid Error': Code ${metadata.error_code} - Message ${metadata.error_message} - Type ${metadata.error_type} - LinkSessionId ${metadata.link_session_id}`
        )
      }
    },
    []
  )

  const config: PlaidLinkOptions = {
    onEvent: onUploadPaystubsEvent,
    onSuccess: () => {
      analytics.logEvent(Analytics.Events.IV_PLAID_LINK_SUCCESS, {
        verificationType: VerificationType.DOCUMENT,
      })
      navigate(Routes.Onboarding.INCOME_VERIFICATION_WAITING_OFFER)
    },
    onExit: (linkExitError) => {
      setPlaidIsOpen(false)
      if (linkExitError?.error_code) {
        analytics.logEvent(Analytics.Events.IV_PLAID_LINK_ERROR, {
          verificationType: VerificationType.DOCUMENT,
        })

        loggerV2.error('Plaid Link Error', JSON.stringify(linkExitError))
        showErrorMessage(errorContent.TITLE, errorContent.SUBTITLE)
      } else {
        analytics.logEvent(Analytics.Events.IV_PLAID_LINK_EXIT, { verificationType: VerificationType.DOCUMENT })
      }
    },
    token: linkToken,
  }

  const { open, ready } = usePlaidLink(config)

  const onClickConnectYourBankAccount = () => {
    analytics.logEvent(Analytics.Events.INCOME_VERIFICATION_CONNECT_YOUR_BANK_CLICKED)

    navigate(Routes.Onboarding.VERIFY_YOUR_INCOME)
  }

  return (
    <UploadPaystubs
      pastThirtyDaysDate={pastThirtyDaysDate}
      nextBpMonth={getNextBpMonth()}
      onClickContinue={onClickContinue}
      onClickConnectYourBankAccount={onClickConnectYourBankAccount}
      isReady={ready}
    />
  )
}
